import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import { confirm, DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import type { BrandDTO } from "../api/client";

type CatalogBrandsTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: BrandDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  onEdit: (row: BrandDTO) => void;
  onDelete: (id: string) => Promise<void>;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function CatalogBrandsTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onEdit,
  onDelete,
  onPaginationChange,
}: CatalogBrandsTableProps) {
  const columns: TanstackReactTable.ColumnDef<BrandDTO>[] = [
    {
      id: "order",
      header: "ລຳດັບ",
      cell: ({ row }) => offset + row.index + 1,
      size: 70,
    },
    {
      id: "name",
      header: "ຊື່ແບຣນດ໌",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
      size: 240,
    },
    {
      id: "createdAt",
      header: "ວັນທີ່ສ້າງ",
      cell: ({ row }) => formatDateTimeLocal(row.original.createdAt),
      size: 120,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        if (!canManage) return null;
        const id = row.original.id;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບແບຣນດ໌",
                description: "ຖ້າມີສິນຄ້າໃຊ້ແບຣນດ໌ນີ້ຢູ່ ຈະລຶບບໍ່ໄດ້. ຕ້ອງການລຶບບໍ?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onDelete(id);
            },
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 120,
    },
  ];

  return (
    <DataTable<BrandDTO, unknown>
      noDataMessage="ບໍ່ພົບຂໍ້ມູນແບຣນດ໌"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
